import Loading from '../../components/Loading';
import React, { useEffect } from 'react';
import { Alert, Box, Button, chakra, Dialog, Field, Flex, Input, InputGroup, Link, Portal, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { FaLock, FaUserAlt } from 'react-icons/fa';
import { setDocumentTitle } from '../../utils/documentTitle';
import { signInWithEmailAndPassword, validatePassword } from 'firebase/auth';
import { useFirebaseAuth } from '../../hooks/useFirebaseAuth';
import { useNavigate } from 'react-router-dom';
import { useSendPasswordResetEmail } from 'react-firebase-hooks/auth';
import { useState } from 'react';
import { useUser } from '../../hooks/useUser';

const CFaUserAlt = chakra(FaUserAlt);
const CFaLock = chakra(FaLock);

export function LoginPage() {
	useEffect(setDocumentTitle('Login')); // eslint-disable-line react-hooks/exhaustive-deps

	const auth = useFirebaseAuth()
	const [user, userLoading] = useUser()
	const [sendPasswordResetEmail] = useSendPasswordResetEmail(auth);
	const navigate = useNavigate();

	useEffect(() => {
		if (user) navigate('/');
	}, [user, navigate]);

	const [email, setEmail] = useState('');
	const [emailInputErrorMessage, setEmailInputErrorMessage] = useState('')
	const handleEmailChange = (e: any) => setEmail(e.target.value);

	const [pass, setPass] = useState('');
	const [passInputErrorMessage, setPassInputErrorMessage] = useState('')
	const handlePassChange = (e: any) => setPass(e.target.value);

	const validateEmail = (email: string) => {
		const isValid = Boolean(String(email)
			.toLowerCase()
			.match(
				/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
			))
		if (isValid)
			setEmailInputErrorMessage('')
		else
			setEmailInputErrorMessage('Wpisany email jest niepoprawny');

	};

	const validatePass = async (pass: string) => {
		const result = await validatePassword(auth, pass)
		if (result.isValid)
			setPassInputErrorMessage('')
		else if (!result.meetsMinPasswordLength)
			setPassInputErrorMessage(
				`Hasło musi mieć co najmniej ${result.passwordPolicy.customStrengthOptions.minPasswordLength} znaków`
			);
		else if (!result.meetsMaxPasswordLength)
			setPassInputErrorMessage(
				`Hasło musi mieć co najwyżej ${result.passwordPolicy.customStrengthOptions.maxPasswordLength} znaków`
			);
	}

	const [showPassword, setShowPassword] = useState(false);
	const handleShowClick = () => setShowPassword(!showPassword);

	const { open, onOpen, onClose } = useDisclosure();
	const cancelRef = React.useRef(null);
	const passRef = React.useRef<HTMLInputElement>(null);

	const handleLogin = async () => {
		// TODO: get values
		try {
			const userInfo = await signInWithEmailAndPassword(auth, email, pass);
			const isAdmin = !!(await userInfo.user.getIdTokenResult()).claims?.admin;
			navigate(isAdmin ? '/admin' : '/panel')
		} catch (err: any) {
			if (err.code === 'auth/invalid-email')
				setEmailInputErrorMessage('Wpisany email jest niepoprawny');
			if (err.code === 'auth/user-not-found')
				setEmailInputErrorMessage('Nie znaleziono konta z podanym adresem email');
			if (err.code === 'auth/missing-password')
				setPassInputErrorMessage('Hasło nie może być puste');
			if (err.code === 'auth/wrong-password')
				setPassInputErrorMessage('Hasło nie jest poprawne');
		}
	};

	if (userLoading || user) {
		return <Loading />;
	}

	return (
		<>
			<Flex
				flexDirection="column"
				width="100wh"
				height="100vh"
				backgroundColor="gray.200"
				justifyContent="center"
				alignItems="center"
			>
				<Stack
					flexDir="column"
					mb="2"
					justifyContent="center"
					alignItems="center"
				>
					<Box minW={{ base: '90%', md: '468px' }}>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								console.log('Submit');
							}}
						>
							<Stack
								gap={4}
								p="1rem"
								backgroundColor="whiteAlpha.900"
								boxShadow="md"
							>
								<Field.Root
									invalid={Boolean(emailInputErrorMessage)}
								>
									<InputGroup startElement={
										<CFaUserAlt pointerEvents="none" color="gray.300" />
									}>
										<Input
											value={email}
											onChange={handleEmailChange}
											autoFocus
											onKeyDown={(e) => {
												if (
													e.key === 'Enter'
												) {
													passRef.current?.focus();
												}
											}}
											onBlur={() => email && validateEmail(email)}
											type="email"
											placeholder="Email"
										/>
									</InputGroup>
									<Field.ErrorText>
										{emailInputErrorMessage}
									</Field.ErrorText>
								</Field.Root>
								<Field.Root
									invalid={Boolean(passInputErrorMessage)}
								>
									<InputGroup
										startElement={<CFaLock pointerEvents="none" color="gray.300" />}
										endElement={
											<Button
												width="4.5rem"
												h="1.75rem"
												size="sm"
												onClick={handleShowClick}
											>
												{showPassword
													? 'Ukryj'
													: 'Pokaż'}
											</Button>
										}>
										<Input
											value={pass}
											ref={passRef}
											onChange={handlePassChange}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													handleLogin();
												}
											}}
											onBlur={() => validatePass(pass)}
											type={
												showPassword
													? 'text'
													: 'password'
											}
											placeholder="Hasło"
										/>
									</InputGroup>
									<Field.ErrorText>
										{passInputErrorMessage}
									</Field.ErrorText>
									<Field.HelperText textAlign="right">
										{email && !emailInputErrorMessage ? (
											<Link onClick={onOpen}>
												zapomniałeś hasła?
											</Link>
										) : (
											<>zapomniałeś hasła?</>
										)}
									</Field.HelperText>
								</Field.Root>
								<Button
									disabled={
										!email ||
										!pass ||
										Boolean(emailInputErrorMessage) ||
										Boolean(passInputErrorMessage)
									}
									borderRadius={0}
									// type="submit"
									variant="solid"
									colorScheme="orange"
									width="full"
									onClick={handleLogin}
								>
									Zaloguj
								</Button>
							</Stack>
						</form>
					</Box>
				</Stack>
			</Flex>
			<Dialog.Root
				role="alertdialog"
				open={open}
				onOpenChange={(details) => {
					if (!details.open) {
						onClose();
					}
				}}
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content>
							<Dialog.Header fontSize="lg" fontWeight="bold">
								Reset hasła
							</Dialog.Header>

							<Dialog.Body>
								<Text>
									Aby dezaktywować aktualne hasło i ustawić nowe wyślemy na adres <strong>{email}</strong> wiadomość email dalszymi instrukcjami.
								</Text>
								<Alert.Root
									size="sm"
									mt={4}
									status="warning"
									colorScheme='gray'
									variant='subtle'
									borderStartWidth='3px'
									borderStartColor='colorPalette.solid'>
									W sporadycznych przypadkach wiadomość może trafić do
									spamu, więc jeśli nie zobaczysz jej w swojej
									skrzynce odbiorczej, sprawdź również tam.
								</Alert.Root>
							</Dialog.Body>

							<Dialog.Footer>
								<Button ref={cancelRef} onClick={onClose}>
									Anuluj
								</Button>
								<Button
									colorScheme="blue"
									onClick={() => {
										sendPasswordResetEmail(email);
										onClose();
										setPass('');
									}}
									ml={3}
								>
									Wyślij
								</Button>
							</Dialog.Footer>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
		</>
	);
}